import { useState } from 'react';
import type { IdeaComment } from '@shared/types';
import { IDEA_LIMITS } from '@shared/validate';
import { relativeTime } from '@shared/format';
import { Button } from '../../../components/Button';
import { TextArea } from '../../../components/Field';
import { EmptyState } from '../../../components/Card';
import { toast } from '../../../components/Toast';
import { overlay } from '../../../overlay';
import { api, errorMessage } from '../../../lib/api';

/**
 * 讨论区。
 *
 * 评论是本平台的「人的判断」出口：研判结论不可编辑，不同意见写在这里。
 * 删除走浮层确认，不用浏览器原生的 confirm。
 */
export function CommentThread({
  ideaId,
  comments,
  currentUserId,
  canDeleteAny,
  onAdded,
  onDeleted,
}: {
  ideaId: string;
  comments: readonly IdeaComment[];
  currentUserId: string;
  canDeleteAny: boolean;
  onAdded: (comment: IdeaComment) => void;
  onDeleted: (commentId: string) => void;
}) {
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const text = body.trim();
    if (text === '') return;
    setSubmitting(true);
    try {
      const comment = await api.post<IdeaComment>(`/api/ideas/${ideaId}/comments`, { body: text });
      onAdded(comment);
      setBody('');
    } catch (error) {
      toast.critical('发表评论失败', errorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (comment: IdeaComment, trigger: HTMLElement) => {
    const confirmed = await overlay.confirm({
      placementKey: `comment.delete.${comment.id}`,
      trigger,
      title: '删除这条评论？',
      description: '删除后无法恢复。创意动态里仍会留下「发表过评论」的记录。',
      confirmText: '删除',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await api.delete(`/api/comments/${comment.id}`);
      onDeleted(comment.id);
      toast.ok('评论已删除');
    } catch (error) {
      toast.critical('删除失败', errorMessage(error));
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {comments.length === 0 ? (
        <EmptyState title="还没有讨论" description="写下你的判断或补充信息，作者与评审员都会看到。" />
      ) : (
        <ul className="flex flex-col gap-3">
          {comments.map((comment) => {
            const mine = comment.authorId === currentUserId;
            return (
              <li key={comment.id} className="flex gap-3">
                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-surface-3 text-[10px] font-semibold text-fg-muted">
                  {comment.authorName.slice(0, 1).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs font-medium text-fg">{comment.authorName}</span>
                    <span className="text-[10px] text-fg-subtle" title={comment.createdAt}>
                      {relativeTime(comment.createdAt)}
                    </span>
                    {mine || canDeleteAny ? (
                      <button
                        type="button"
                        className="ml-auto text-[10px] text-fg-subtle hover:text-critical"
                        onClick={(event) => void remove(comment, event.currentTarget)}
                      >
                        删除
                      </button>
                    ) : null}
                  </div>
                  <p className="idea-body mt-1 text-xs leading-relaxed text-fg-muted">{comment.body}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-col gap-2">
        <TextArea
          label="发表评论"
          rows={3}
          maxLength={IDEA_LIMITS.commentMax}
          placeholder="补充依据、提出反对意见、或者说明你为什么认领它。"
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-fg-subtle">
            {body.trim().length}/{IDEA_LIMITS.commentMax}
          </span>
          <Button variant="primary" size="sm" loading={submitting} disabled={body.trim() === ''} onClick={() => void submit()}>
            发表
          </Button>
        </div>
      </div>
    </div>
  );
}
